import { Table, TableHeader, TableBody, Pagination } from '../../../components/table';
import Search from '../../../components/table/Search';
import StatusBadge from '../../../components/ui/StatusBadge';
import { Dialog } from '../../../components/ui';
import React, { useEffect, useRef, useState } from 'react'

import { coursePerformanceDetails } from '../../../services/organization/courses/courseAnalytics.service';
import NoData from '../../../components/common/NoData';
import { decodeData, encodeData } from '../../../utils/encodeAndEncode';
import defaultProfile from '../../../assets/images/profile/default-profile.png'
import { Link, useParams } from 'react-router-dom';
import { usePermissions } from '../../../context/PermissionsContext';
import { getSignedMediaUrl } from '../../../services/media.service';
import { toast } from '../../../components/ui/notify';

function clampProgressPct(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 0;
}

function CourseAnalyticsTraineeProgress() {
  const { courseId } = useParams();
  const { permissions } = usePermissions();
  const decodedCourseId = decodeData(courseId);

  const [traineesData, setTraineesData] = useState([]);
  const [certificateSummary, setCertificateSummary] = useState(null);
  const [certificateActionId, setCertificateActionId] = useState(null);
  const [certificatePreview, setCertificatePreview] = useState(null);
  const previewRequestRef = useRef(0);
  const previewUrlRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 0,
    totalCount: 0
  });

  // Fetch trainee progress data
  useEffect(() => {
    fetchTraineeProgress();
  }, [decodedCourseId, search, statusFilter, pagination.page, pagination.limit]);

  const fetchTraineeProgress = async () => {
    try {
      setLoading(true);
      const response = await coursePerformanceDetails({
        course_id: decodedCourseId,
        search: search,
        page: pagination.page,
        limit: pagination.limit,
        status: statusFilter
      });


      if (response.success && response.data) {
        setTraineesData(response.data.users || []);
        setCertificateSummary(response.data.certificate_summary || null);
        setPagination(prev => ({
          ...prev,
          page: response.data.pagination?.current_page || 1,
          totalPages: response.data.pagination?.total_pages || 0,
          totalCount: response.data.pagination?.total_records || 0
        }));
      }
    } catch (error) {
      console.error('Error fetching trainee progress:', error);
      setCertificateSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const traineeAnalyticsPath = (trainee, tab = 'courses') =>
    `/admin/traineesAnalytics/${encodeData(String(trainee.user_id))}?tab=${tab}`;

  const closeCertificatePreview = () => {
    previewRequestRef.current += 1;
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = null;
    setCertificatePreview(null);
  };

  useEffect(() => () => {
    previewRequestRef.current += 1;
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const handleViewCertificate = async (trainee) => {
    const certificateId = trainee.certificate_view_id;
    if (!certificateId) return;
    const requestId = ++previewRequestRef.current;
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = null;
    setCertificatePreview({ trainee, loading: true, url: null, error: false });
    try {
      const url = await getSignedMediaUrl({ type: 'certificate', mediaId: certificateId, kind: 'web' });
      if (!url) throw new Error('Certificate file is unavailable');
      const response = await fetch(url);
      if (!response.ok) throw new Error('Certificate preview failed');
      const blob = await response.blob();
      if (requestId !== previewRequestRef.current) return;
      const objectUrl = URL.createObjectURL(blob);
      previewUrlRef.current = objectUrl;
      const mimeType = blob.type || response.headers.get('content-type') || '';
      const isImage = mimeType.startsWith('image/') || /\.(png|jpe?g|webp|gif)(?:[?#]|$)/i.test(trainee.certificate_url || '');
      setCertificatePreview({ trainee, loading: false, url: objectUrl, isImage, error: false });
    } catch {
      if (requestId === previewRequestRef.current) {
        setCertificatePreview({ trainee, loading: false, url: null, error: true });
      }
    }
  };

  const handleDownloadCertificate = async (trainee) => {
    const certificateId = trainee.certificate_view_id;
    if (!certificateId) return;
    setCertificateActionId(`${certificateId}:download`);
    try {
      const url = await getSignedMediaUrl({ type: 'certificate', mediaId: certificateId, kind: 'pdf' });
      if (!url) throw new Error('Certificate file is unavailable');
      const response = await fetch(url);
      if (!response.ok) throw new Error('Certificate download failed');
      const objectUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${String(trainee.user_name || 'trainee').replace(/[^\w\s-]/g, '').trim() || 'trainee'}-certificate.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      toast.error('Unable to download this certificate.');
    } finally {
      setCertificateActionId(null);
    }
  };


  return (
    <>
      {/* Search and Filter */}
      <div className={"tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-flex tw-mb-4"}>
        <div className="max-[768px]:!tw-w-[50%] tw-min-w-[0px] ui-column md:tw-w-1/2 tw-mt-2 tw-flex tw-justify-start tw-items-center">
          <Search
            type="search"
            className={"[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input"}
            placeholder="Search trainees..."
            style={{ maxWidth: '300px' }}
            value={search}
            onChange={handleSearchChange}
          />
        </div>
        <div className="max-[768px]:!tw-w-[50%] tw-min-w-[0px] ui-column md:tw-w-1/2 tw-text-right tw-mt-2 tw-flex tw-justify-end tw-items-center tw-gap-2">
          <select
            className={"[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [appearance:none] tw-pe-[2.25rem] tw-bg-[url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScyMCcgaGVpZ2h0PScyMCcgZmlsbD0nbm9uZScgc3Ryb2tlPScjNjQ3NDhiJyB2aWV3Qm94PScwIDAgMjQgMjQnPjxwYXRoIGQ9J203IDEwIDUgNSA1LTUnLz48L3N2Zz4=)] tw-bg-[right_.65rem_center] tw-bg-no-repeat [&[multiple]]:tw-bg-[none] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-select"}
            style={{ width: '180px' }}
            value={statusFilter}
            onChange={handleStatusFilterChange}
          >
            <option value="">All Progress</option>
            <option value="completed">Completed</option>
            <option value="in_progress">In Progress</option>
            <option value="not_started">Not Started</option>
          </select>
        </div>
      </div>

      {!loading && certificateSummary?.template_available === false && (
        <div role="status" className="tw-mb-4 tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-4 tw-py-3 tw-text-sm tw-text-amber-900">
          No active certificate template is available for this course. Configure a template before issuing certificates.
        </div>
      )}
      {!loading && certificateSummary?.pending_count === 0 && (
        <div role="status" className="tw-mb-4 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-4 tw-py-3 tw-text-sm tw-text-slate-600">
          No completed trainees are currently waiting for a certificate.
        </div>
      )}

      {/* Table */}
      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-mb-4"}>
        <div className="tw-min-w-[0px] ui-column tw-w-full">
          <div className={"[&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card"}>
            <div className="tw-w-full tw-overflow-x-auto tw-rounded-[0.75rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [-webkit-overflow-scrolling:touch] ui-table-scroll">
              <Table className={"[&_.deleted-accounts-actions_.ui-button-sm]:!tw-w-auto [&_.deleted-accounts-actions_.ui-button-sm]:!tw-h-auto [&_.deleted-accounts-actions_.ui-button-sm]:!tw-min-h-[32px] [&_.deleted-accounts-actions_.ui-button-sm]:!tw-p-[0.45rem_1.25rem] [&_.deleted-accounts-actions_.ui-button-sm]:!tw-inline-flex [&_.deleted-accounts-actions_.ui-button-sm]:!tw-items-center [&_.deleted-accounts-actions_.ui-button-sm]:!tw-justify-center [&_.deleted-accounts-actions_.btn-view]:!tw-min-w-[36px] [&_.deleted-accounts-actions_.btn-view]:!tw-p-[0.45rem_0.7rem] [&_.deleted-accounts-actions_.btn-restore]:!tw-min-w-[92px] [&_.deleted-accounts-actions_.btn-restore]:!tw-pl-[1.35rem] [&_.deleted-accounts-actions_.btn-restore]:!tw-pr-[1.35rem] [&_.deleted-accounts-actions_.btn-delete]:!tw-min-w-[92px] [&_.deleted-accounts-actions_.btn-delete]:!tw-pl-[1.35rem] [&_.deleted-accounts-actions_.btn-delete]:!tw-pr-[1.35rem] tw-w-full tw-border-collapse tw-text-left tw-text-[0.875rem] tw-leading-[1.25rem] [&_>_thead]:[--tw-bg-opacity:1] [&_>_thead]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&_th]:tw-whitespace-nowrap [&_th]:[--tw-border-opacity:1] [&_th]:tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [&_th]:tw-pl-4 [&_th]:tw-pr-4 [&_th]:tw-pt-3 [&_th]:tw-pb-3 [&_th]:tw-text-[0.75rem] [&_th]:tw-leading-[1rem] [&_th]:tw-font-semibold [&_th]:[--tw-text-opacity:1] [&_th]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [&_td]:[--tw-border-opacity:1] [&_td]:tw-border-[rgb(241_245_249_/_var(--tw-border-opacity,_1))] [&_td]:tw-pl-4 [&_td]:tw-pr-4 [&_td]:tw-pt-[0.875rem] [&_td]:tw-pb-[0.875rem] [&_td]:tw-align-middle [&_td]:[--tw-text-opacity:1] [&_td]:tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&_tbody_tr:last-child_td]:tw-border-b-[0px] [&_tbody_tr]:[transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [&_tbody_tr]:[transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [&_tbody_tr]:[transition-duration:150ms] [&_tbody_tr:hover]:[--tw-bg-opacity:1] [&_tbody_tr:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&_th]:tw-border-[0px] [&_td]:tw-border-[0px] ui-table ui-table-borderless tw-mb-0"}>
                <TableHeader>
                  <tr>
                    <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500' }}>SL.No</th>
                    <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500' }}>User</th>
                    <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500' }}>Course Completion</th>
                    <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500' }}>Status</th>
                    <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500' }}>Certificate</th>
                  </tr>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="tw-text-center tw-py-6">
                        <div className="!tw-text-[var(--primary-color)] tw-w-[1.5em] tw-h-[1.5em] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] ui-spinner tw-text-brand-600" role="status">
                          <span className="tw-sr-only">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  ) : traineesData.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="tw-p-0">
                        <NoData message={search ? 'No trainees match your search' : statusFilter === 'completed' ? 'No completed trainees in this course' : statusFilter === 'in_progress' ? 'No trainees are in progress' : statusFilter === 'not_started' ? 'No trainees are waiting to start' : 'No trainees enrolled in this course'} />
                      </td>
                    </tr>
                  ) : (
                    traineesData.map((trainee, index) => {
                      const progressPct = clampProgressPct(trainee.overallCompletionPercentage);
                      const doneBar = Number(trainee.course_completed) === 1;
                      const canViewTrainee = Boolean(permissions.trainee_management_view_analytics && trainee.user_id);
                      return (
                      <tr key={trainee.user_id || index} className="tw-border-b">
                        <td className="tw-py-4 tw-align-middle">
                          {(pagination.page - 1) * pagination.limit + index + 1}
                        </td>
                        <td className="tw-py-4">
                          <div className="tw-flex tw-items-center">
                            <img
                              src={trainee.profile_pic_url || defaultProfile}
                              alt={trainee.user_name || 'User'}
                              className="tw-rounded-full tw-me-4"
                              width="40"
                              height="40"
                              onError={(e) => {
                                e.target.src = defaultProfile;
                              }}
                            />
                            <div>
                              {canViewTrainee ? (
                                <>
                                  <Link className="tw-block tw-text-left tw-font-medium tw-text-indigo-700 hover:tw-underline" to={traineeAnalyticsPath(trainee)}>
                                    {trainee.display_name || trainee.user_name || 'N/A'}
                                  </Link>
                                  <Link className="tw-block tw-break-all tw-text-left tw-text-xs tw-text-slate-500 hover:tw-text-indigo-700 hover:tw-underline" to={traineeAnalyticsPath(trainee)}>
                                    {trainee.email || 'N/A'}
                                  </Link>
                                </>
                              ) : (
                                <>
                                  <div className="tw-font-medium">{trainee.display_name || trainee.user_name || 'N/A'}</div>
                                  <div className="tw-break-all tw-text-xs tw-text-slate-500">{trainee.email || 'N/A'}</div>
                                </>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="tw-py-4 tw-align-middle" style={{ minWidth: '160px', maxWidth: '260px' }}>
                          <div className="tw-flex tw-items-center tw-gap-2">
                            <div
                              className="tw-bg-[rgb(241_245_249_/_var(--tw-bg-opacity,_1))] tw-overflow-hidden tw-flex tw-h-2 [--tw-bg-opacity:1] ui-progress tw-rounded-full tw-grow"
                              style={{ height: '8px', backgroundColor: '#e9ecef', minWidth: '100px' }}
                            >
                              <div
                                className={`tw-h-full tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [transition:width_0.3s_ease] tw-flex tw-items-center tw-justify-center [--tw-bg-opacity:1] tw-text-[0.75rem] tw-leading-[1rem] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [transition-property:width] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:300ms] ui-progress-bar tw-rounded-full   ${doneBar ? "tw-bg-emerald-600" : "!tw-bg-[var(--primary-color)] tw-bg-brand-600"}`}
                                role="progressbar"
                                style={{
                                  width: `${progressPct}%`,
                                  transition: 'width 0.35s ease',
                                }}
                                aria-valuenow={progressPct}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              />
                            </div>
                            <span className="!tw-text-[12px] tw-text-sm tw-whitespace-nowrap">{Math.floor(progressPct)}%</span>
                          </div>
                        </td>
                        <td className="tw-py-4 tw-align-middle">
                          <StatusBadge tone={doneBar ? 'success' : progressPct > 0 ? 'warning' : 'neutral'}>
                            {doneBar ? 'Completed' : progressPct > 0 ? 'In Progress' : 'Not Started'}
                          </StatusBadge>
                        </td>
                        <td className="tw-py-4 tw-align-middle">
                          {trainee.certificate_generated ? (
                            <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                              {trainee.certificate_view_id ? (
                                <button type="button" className="tw-rounded-full focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-emerald-600" onClick={() => handleViewCertificate(trainee)} aria-label={`View certificate for ${trainee.display_name || trainee.user_name || 'trainee'}`} title="View certificate">
                                  <StatusBadge tone="success" className="tw-cursor-pointer hover:tw-bg-emerald-100">Generated</StatusBadge>
                                </button>
                              ) : <StatusBadge tone="success">Generated</StatusBadge>}
                              {trainee.certificate_issue_date && <span className="tw-text-xs tw-text-slate-500">{new Date(trainee.certificate_issue_date).toLocaleDateString()}</span>}
                              {trainee.certificate_view_id && (
                                <div className="tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-1 tw-text-sm">
                                  <button type="button" className="tw-font-medium tw-text-indigo-700 hover:tw-underline" disabled={Boolean(certificateActionId)} onClick={() => handleDownloadCertificate(trainee)}>Download</button>
                                  {canViewTrainee && <Link className="tw-font-medium tw-text-indigo-700 hover:tw-underline" to={traineeAnalyticsPath(trainee, 'certificates')}>Records</Link>}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                              <StatusBadge tone="neutral">Not Generated</StatusBadge>
                              {canViewTrainee && <Link className="tw-text-sm tw-font-medium tw-text-indigo-700 hover:tw-underline" to={traineeAnalyticsPath(trainee, 'certificates')}>Certificate records</Link>}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {!loading && traineesData.length > 0 && (
        <Pagination page={pagination.page} pageSize={pagination.limit} totalRecords={pagination.totalCount} totalPages={pagination.totalPages} onPageChange={handlePageChange} onPageSizeChange={(size) => setPagination(prev => ({ ...prev, limit: size, page: 1 }))} />
      )}

      <Dialog show={Boolean(certificatePreview)} onHide={closeCertificatePreview} size="xl" scrollable>
        <Dialog.Header closeButton>
          <Dialog.Title>Certificate — {certificatePreview?.trainee?.display_name || certificatePreview?.trainee?.user_name || 'Trainee'}</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body className="tw-bg-slate-50 tw-p-4">
          {certificatePreview?.loading ? (
            <div className="tw-flex tw-min-h-[20rem] tw-items-center tw-justify-center tw-text-slate-600" role="status">Loading certificate...</div>
          ) : certificatePreview?.error ? (
            <div className="tw-flex tw-min-h-[12rem] tw-items-center tw-justify-center tw-text-slate-600" role="alert">Unable to show this certificate. Please try again.</div>
          ) : certificatePreview?.url && (
            <div className="tw-flex tw-min-h-[20rem] tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white">
              {certificatePreview.isImage ? (
                <img src={certificatePreview.url} alt={`Certificate for ${certificatePreview.trainee.display_name || certificatePreview.trainee.user_name || 'trainee'}`} className="tw-max-h-[70vh] tw-max-w-full tw-object-contain" />
              ) : (
                <iframe src={certificatePreview.url} title={`Certificate for ${certificatePreview.trainee.display_name || certificatePreview.trainee.user_name || 'trainee'}`} className="tw-h-[70vh] tw-w-full tw-border-0" />
              )}
            </div>
          )}
        </Dialog.Body>
        <Dialog.Footer>
          {certificatePreview?.trainee?.certificate_view_id && (
            <button type="button" className="tw-rounded-lg tw-bg-indigo-600 tw-px-4 tw-py-2 tw-font-medium tw-text-white hover:tw-bg-indigo-700 disabled:tw-opacity-50" disabled={Boolean(certificateActionId)} onClick={() => handleDownloadCertificate(certificatePreview.trainee)}>Download</button>
          )}
          <button type="button" className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-font-medium tw-text-slate-700 hover:tw-bg-slate-50" onClick={closeCertificatePreview}>Close</button>
        </Dialog.Footer>
      </Dialog>
    </>
  )
}

export default CourseAnalyticsTraineeProgress
